<template>
  <div>
    <!-- STATUS -->
    <div class="panel" v-if="sections.includes('status')">
      <header class="panel__header">
        <div class="profile-compact">
          <div class="logo-container">
            <font-awesome-icon icon="check" class="icon-svg--sm"/>
          </div>
          <div class="profile-compact__info">
            <div class="profile-compact__top">
              <div class="profile-compact__text">
                {{ $t('system.status') }}
                <span class="section-compact__secondary"></span>
              </div>
            </div>
          </div>
        </div>

        <div class="profile-compact__actions">
          <span class="mr-3 btn btn-success cursor-pointer"
                style="top: -8px"
                @click="$emit('complete', activity)"
                data-qid="complete-project"
                v-if="canManage && activity.control_type === 'one time' && activity.type !== 'control_assessment' && activity.type !== 'risk_assessment' && activity.type !== 'bia_assessment' && (activity.status == null || activity.status === 'pending' || activity.status === 'in progress') && activity.progress === 100"
          >
              {{ $t('projects.button.complete_project') }}
          </span>
            <span class="mr-3 btn btn-success cursor-pointer"
                  style="top: -8px"
                  @click="$emit('complete', activity)"
                  data-qid="complete-project"
                  v-if="canManage && activity.control_type === 'one time' && (activity.type === 'control_assessment' || activity.type === 'risk_assessment' || activity.type === 'bia_assessment') && (activity.status == null || activity.status === 'pending' || activity.status === 'in progress') && activity.progress === 100"
            >
            {{ $t('projects.button.complete_project') }}
          </span>
            <span class="mr-3 btn btn-light cursor-pointer"
                  style="top: -8px"
                  @click="$emit('reopen', activity)"
                  data-qid="close-project"
                  v-if="canManage && activity.control_type === 'one time' && (activity.status === 'closed' || activity.status === 'completed')"
            >
            {{ $t('projects.button.reopen_project') }}
         </span>
          <span class="mr-3 btn btn-light cursor-pointer"
                style="top: -8px"
                @click="$emit('close', activity)"
                data-qid="close-project"
                v-if="canManage && activity.control_type === 'one time' && (activity.status == null || activity.status === 'pending' || activity.status === 'in progress')"
          >
            {{ $t('projects.button.close_project') }}
          </span>
        </div>
      </header>

      <div class="panel__body">
        <span v-if="activity.control_type === 'one time'">
          <span v-if="activity.status === 'completed'" class="pr-1">
            <font-awesome-icon icon="share-square" class="risk-1"/>
          </span>
          <span v-if="activity.status === 'completed'">
            {{ $t('projects.button.completed_label') }}
          </span>

          <span v-if="activity.status === 'pending' || activity.status === null" class="pr-1">
            <font-awesome-icon icon="exclamation-triangle" class="risk-1"/>
          </span>
          <span v-if="activity.status === 'pending' || activity.status === null">
            {{ $t('system.pending') }}
          </span>

          <span v-if="activity.status === 'in progress'" class="pr-1">
            <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
          </span>
          <span v-if="activity.status === 'in progress'">
            {{ $t('system.in_progress') }}
          </span>

          <span v-if="activity.status === 'closed'" class="pr-1">
            <font-awesome-icon icon="archive" class="text-muted"/>
          </span>
          <span v-if="activity.status === 'closed'">
            {{ $t('projects.button.closed_label') }}
          </span>
        </span>
        <span v-if="activity.control_type === 'continuous'">
          <span v-if="!activity.is_active_child_project" class="pr-1">
            <font-awesome-icon icon="exclamation-triangle" class="risk-1"/>
            {{ $t('system.pending') }}
          </span>
          <span v-if="activity.is_active_child_project" class="pr-1">
            <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
            {{ $t('system.in_progress') }}
          </span>
        </span>
      </div>
    </div>

    <!-- SCOPE -->
    <div class="panel" v-if="sections.includes('scope')">
      <ControlAssessmentScopeTable
          :object="activity"
          @updated="$emit('updated')"
          :measureTask="measureTask"
      ></ControlAssessmentScopeTable>
    </div>

    <!-- SCHEDULE: timeline and dates as on the Overview; editing shows the date fields -->
    <div class="v2-card schedule-v2" :class="{'schedule-v2--editing': editingSchedule}" v-if="sections.includes('schedule') && activity.control_type === 'one time'" data-qid="planning-schedule">
      <div class="v2-card__head">
        <h3>{{ $t('projects.tab.schedule') }}</h3>
        <span v-if="!editingSchedule" class="v2-tag" :class="activity.overdue_notifications ? 'v2-tag--teal' : 'v2-tag--grey'">
          <font-awesome-icon :icon="activity.overdue_notifications ? 'bell' : 'bell-slash'" class="mr-1"/>{{ activity.overdue_notifications ? $t('projects.engagement.overview.reminders_on') : $t('projects.engagement.overview.reminders_off') }}
        </span>
        <span class="icon-btn"
              data-qid="schedule-s-button"
              v-if="!editingSchedule && canEditSchedule"
              @click="startEditingSchedule"
              v-b-tooltip.hover
              :title="$t('system.edit')"
        ><font-awesome-icon icon="pen"/></span>
      </div>

      <div class="v2-card__body">
        <b-alert v-if="scheduleError.length"
                 show
                 variant="danger"
                 data-qid="schedule-error"
        >{{ scheduleError }}</b-alert>

        <EngagementScheduleCard v-if="!editingSchedule" :project="activity" embedded></EngagementScheduleCard>

        <template v-else>
          <div class="schedule-v2__fields">
            <div v-for="field in dateFields"
                 :key="'edit-' + field.key"
                 class="schedule-v2__field"
                 :data-qid="'schedule-date-' + field.key"
            >
              <label><font-awesome-icon :icon="field.icon" class="mr-1"/>{{ $t(field.label) }}</label>
              <datepicker class="date-picker"
                          calendar-class="left"
                          v-model="scheduleForm[field.key]"
                          :clear-button="true"
                          :placeholder="$t(field.label)"
                          @selected="field.key === 'start_date' && startDateSelected($event)"
              ></datepicker>
            </div>
          </div>
          <label class="schedule-v2__label">{{ $t('projects.create.overdue_reminder') }}</label>
          <div class="task-choices" data-qid="schedule-overdue-reminders">
            <span v-for="option in yesNoOptions"
                  :key="'reminders-' + option.value"
                  class="task-choice task-choice--teal"
                  :class="{'task-choice--on': scheduleForm.overdue_notifications === option.value}"
                  @click="scheduleForm.overdue_notifications = option.value"
            >
              <font-awesome-icon :icon="option.value ? 'bell' : 'bell-slash'"/>{{ option.text }}
            </span>
          </div>
          <div class="v2-field__actions">
            <b-button size="sm"
                      variant="secondary"
                      @click="editingSchedule = false"
                      data-qid="schedule-cancel-button"
            >{{ $t('projects.create.cancel') }}</b-button>
            <b-button size="sm"
                      variant="success"
                      @click="saveSchedule"
                      data-qid="schedule-save-button"
            >{{ $t('tasks.modal.save') }}</b-button>
          </div>
        </template>
      </div>
    </div>

  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import Datepicker from 'vuejs-datepicker';
import ControlAssessmentScopeTable from '@/views/Projects/ControlAssessmentScopeTable.vue';
import EngagementScheduleCard from '@/views/Projects/Engagement/EngagementScheduleCard.vue';

// Status, scope and schedule panels of a project, shown on Basic info or (engagements) on the Settings tab
export default {
  name: 'ProjectSettingsPanels',
  props: {
    activity: {
      type: Object,
      required: true
    },
    sections: {
      type: Array,
      default: () => ['status', 'scope', 'schedule']
    },
    measureTask: {}
  },
  components: {
    Datepicker,
    ControlAssessmentScopeTable,
    EngagementScheduleCard
  },
  data: function() {
    return {
      editingSchedule: false,
      scheduleError: '',
      scheduleForm: {},
      yesNoOptions: [
        { text: this.$t('system.yes'), value: 1 },
        { text: this.$t('system.no'), value: 0 }
      ]
    }
  },
  computed: {
    canEditSchedule() {
      return this.isGranted([this.permissions.PROJECT_EDIT, this.permissions.PROJECT_EDIT_OWN], this.activity) && this.canManage && this.activity.status !== 'closed' && this.activity.status !== 'completed';
    },
    dateFields() {
      let fields = [{ key: 'start_date', label: 'projects.schedule.start_date_label', icon: 'play' }];
      if (this.activity.type === 'control_assessment') {
        fields.push({ key: 'data_collection_end_date', label: 'projects.schedule.data_due_date_label', icon: 'folder-open', due: true });
      }
      fields.push({ key: 'end_date', label: 'projects.schedule.due_date_label', icon: 'calendar-alt', due: true });
      return fields;
    },
    canManage() {
      // On engagements only Managers change the schedule
      return !this.activity.engagement_permissions || !!this.activity.engagement_permissions.manage;
    }
  },
  methods: {
    startEditingSchedule() {
      this.scheduleError = '';
      this.scheduleForm = {
        overdue_notifications: this.activity.overdue_notifications ? 1 : 0,
        start_date: this.localDate(this.activity.start_date),
        end_date: this.localDate(this.activity.end_date),
        data_collection_end_date: this.localDate(this.activity.data_collection_end_date)
      };
      this.editingSchedule = true;
    },
    // Due dates before a new start date no longer make sense
    startDateSelected(date) {
      _.forEach(['end_date', 'data_collection_end_date'], key => {
        if (date && this.scheduleForm[key] && this.dateValue(this.scheduleForm[key]) < this.dateValue(date)) {
          this.scheduleForm[key] = '';
        }
      });
    },
    // The calendar day of an API date as a local date, so the datepicker shows the same day in every time zone
    localDate(value) {
      if (!value) {
        return '';
      }
      const parts = String(value).substring(0, 10).split('-');
      return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    },
    // YYYY-MM-DD in local time, for dates from the datepicker and from the API
    dateValue(value) {
      if (!value) {
        return '';
      }
      return value instanceof Date ? value.toLocaleDateString('en-CA') : String(value).substring(0, 10);
    },
    saveSchedule() {
      let self = this;
      const form = {
        start_date: this.dateValue(this.scheduleForm.start_date),
        end_date: this.dateValue(this.scheduleForm.end_date),
        data_collection_end_date: this.dateValue(this.scheduleForm.data_collection_end_date)
      };

      let errors = [];
      _.forEach(this.dateFields, field => {
        if (!form[field.key]) {
          errors.push(this.$t({
            start_date: 'projects.schedule.start_date_error',
            end_date: 'projects.schedule.end_date_error',
            data_collection_end_date: 'projects.schedule.data_end_date_error'
          }[field.key]));
        }
      });
      if (!errors.length && form.start_date > form.end_date) {
        errors.push(this.$t('projects.schedule.start_date_greater_error'));
      }
      if (errors.length) {
        this.scheduleError = errors.join(' ');
        return;
      }

      // Only changed dates are sent: saving a start date also starts the project and notifies the assignees
      let payload = { overdue_notifications: this.scheduleForm.overdue_notifications };
      _.forEach(this.dateFields, field => {
        if (form[field.key] !== this.dateValue(this.activity[field.key])) {
          payload[field.key] = form[field.key];
        }
      });

      piincHttp.put('projects/' + this.activity.slug, payload).then(function() {
        self.editingSchedule = false;
        self.scheduleError = '';
        self.$toastr('success', self.$t('projects.update.success'));
        self.$emit('updated');
      }, function(error) {
        self.scheduleError = error.data && error.data.message ? error.data.message : '';
      });
    }
  }
}
</script>
